// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Placement } from "@floating-ui/react";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FloatingTooltip, {
    FloatingTooltipProps,
    FloatingTooltipStyles,
} from "@werner/App/Components/FloatingTooltip/FloatingTooltip";
import * as React from "react";
import { FC } from "react";
import { withTheme } from "styled-components";

// XNG-Changes: All OOTB are cleaned up we're currently using Floating UI

export interface TooltipPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS strings or styled-components functions to be injected into nested components. These will override the theme defaults.
     * @themable */
    cssOverrides?: {
        // tooltipClickable?: StyledProp<TooltipProps>;
        // tooltipBody?: StyledProp<TooltipProps>;
        // tooltipContainer?: StyledProp<TooltipProps>;
        // tooltipWrapper?: StyledProp<TooltipProps>;
        // tooltipArrow?: StyledProp<TooltipProps>;
    };
    /** Props to be passed into the trigger Icon component if no triggerComponent is provided.
     * @themable */
    iconProps?: IconPresentationProps;
    /** Props that will be passed to the typography component rendered in the tooltip.
     * @themable */
    typographyProps?: TypographyPresentationProps;
    // XNG-Changes
    extendedStyles?: FloatingTooltipStyles;
}

export type TooltipComponentProps = MobiusStyledComponentProps<
    "span",
    {
        /** Component to be used as the trigger to optionally replace the default trigger icon. */
        triggerComponent?: React.ReactElement<{ tabIndex: number }>;
        /** The text that will appear in the tooltip */
        text: string | React.ReactElement | React.ReactNode | React.ReactNode[];

        // /** Function called on tooltip open */
        // onOpen?: React.EventHandler<React.SyntheticEvent>;
        // /** Function called on tooltip close */
        // onClose?: React.EventHandler<React.SyntheticEvent>;
        /** Text to be used to describe the tooltip trigger. Used for screenreaders. Defaults to "more info" */
        triggerAltText?: string;

        trigger?: "hover" | "click" | "manual";
        // XNG-Changes
        onClick?: () => void;
        placement?: Placement;
        onTrigger?: FloatingTooltipProps["onOpenChange"];
    }
>;

export type TooltipProps = TooltipComponentProps & TooltipPresentationProps;

/**
 * Component that provides supplementatal information on click or enter.
 */
const Tooltip: FC<TooltipProps> = props => {
    const { text, triggerComponent, triggerAltText, trigger = "hover", onClick, mergeCss } = props;
    const { spreadProps, applyStyledProp } = applyPropBuilder(props, { component: "tooltip" });
    // XNG-Changes
    const resolvedMergeCss = mergeCss ?? props?.theme?.tooltip?.defaultProps?.mergeCss;

    if (!text || (typeof text === "string" && text.length === 0)) {
        return null;
    }

    let triggerElement;
    if (triggerComponent) {
        /** remove tabability because of `Clickable` wrapper to remove redundant tab if the triggerComponent
         * is a tabbable component. */
        triggerElement = React.cloneElement(triggerComponent, { tabIndex: -1 });
    } else {
        triggerElement = <IconMemo {...spreadProps("iconProps")} />;
    }

    // XNG-Changes
    const triggerWrapper = (
        <span onClick={() => onClick?.()}>
            {triggerElement}
            <VisuallyHidden>{triggerAltText || props.theme!.translate("more info")}</VisuallyHidden>
        </span>
    );

    return (
        <FloatingTooltip
            trigger={trigger}
            overlay={false}
            placement={props.placement}
            triggerComponent={triggerWrapper}
            extendedStyles={{
                ...spreadProps("extendedStyles"),
                tooltipContent: {
                    css: applyStyledProp(["extendedStyles", "tooltipContent", "css"], resolvedMergeCss),
                },
                triggerWrapper: {
                    css: applyStyledProp(["extendedStyles", "triggerWrapper", "css"], resolvedMergeCss),
                },
            }}
            onOpenChange={props.onTrigger}
        >
            <div aria-live="polite" data-test-selector="tooltip_TooltipInfo" className="tooltipInfo">
                <Typography as="p" {...spreadProps("typographyProps")} data-test-selector="toolTip">
                    {text}
                </Typography>
            </div>
        </FloatingTooltip>
    );
};

/** @component */
export default withTheme(Tooltip);
